import React, { useState, useEffect } from "react";
import { Box, Typography, Stack, useTheme, Paper, Tooltip } from "@mui/material";

const formatTimeHM = (time) => {
  if (!time) return "";
  return String(time).slice(0, 5);
};

export default function VistaSemana({
  currentDate,
  events,
  searchTerm,
  onDateClick,
  onEventClick,
  isToday,
}) {
  const theme = useTheme();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 60000); // Actualizar cada minuto
    return () => clearInterval(timer);
  }, []);

  // Obtener el inicio de la semana (Lunes)
  const startOfWeek = new Date(currentDate);
  const dayIndex = currentDate.getDay();
  startOfWeek.setDate(currentDate.getDate() - (dayIndex === 0 ? 6 : dayIndex - 1));

  const daysOfWeek = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    daysOfWeek.push(day);
  }

  const getEventsForDay = (date) => {
    const y = date.getFullYear();
    const m = date.getMonth();
    const d = date.getDate();
    const cellDateStr = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    
    return events.filter((event) => {
      if (searchTerm && !event.titulo.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (!event.fecha) return false;
      if (typeof event.fecha === "string") return event.fecha.startsWith(cellDateStr);
      const eventDate = new Date(event.fecha);
      return eventDate.getDate() === d && eventDate.getMonth() === m && eventDate.getFullYear() === y;
    });
  };

  const DAYS_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
  const hours = Array.from({ length: 24 }, (_, i) => i); // 00:00 a 23:00
  const hourHeight = 42; // Píxeles por hora
  const dayHeight = hours.length * hourHeight;
  const scrollbarWidth = 12;
  const hourLineOffset = 0;

  const getEventPosition = (evt) => {
    const defaultTop = 0;
    const defaultHeight = hourHeight;
    if (!evt.horaInicio) return { top: defaultTop, height: defaultHeight };
    
    const [startH, startM] = evt.horaInicio.split(':').map(Number);
    const top = (startH * hourHeight) + (startM * hourHeight / 60);
    
    let height = defaultHeight;
    if (evt.horaFin) {
      const [endH, endM] = evt.horaFin.split(':').map(Number);
      const normalizedEndH = endH < startH || (endH === startH && endM <= startM) ? endH + 24 : endH;
      height = ((normalizedEndH - startH) * hourHeight) + ((endM - startM) * hourHeight / 60);
    }

    const safeTop = Math.max(0, top);
    const unclampedHeight = Math.max(height, 25);
    const maxHeight = Math.max(0, dayHeight - safeTop);
    const clampedHeight = Math.min(unclampedHeight, maxHeight);

    return {
      top: safeTop,
      height: clampedHeight,
      continuesAfterDay: safeTop + unclampedHeight > dayHeight,
    };
  };

  const layoutTimedEvents = (timedEvents) => {
    const positionedEvents = timedEvents
      .map((evt) => {
        const position = getEventPosition(evt);
        return {
          evt,
          ...position,
          end: position.top + position.height,
          column: 0,
          columnCount: 1,
        };
      })
      .sort((a, b) => a.top - b.top || a.end - b.end);

    const groups = [];
    positionedEvents.forEach((item) => {
      const currentGroup = groups[groups.length - 1];
      if (!currentGroup || item.top >= currentGroup.end) {
        groups.push({ end: item.end, items: [item] });
        return;
      }

      currentGroup.items.push(item);
      currentGroup.end = Math.max(currentGroup.end, item.end);
    });

    groups.forEach((group) => {
      const columnEnds = [];
      group.items.forEach((item) => {
        const column = columnEnds.findIndex((end) => end <= item.top);
        const targetColumn = column === -1 ? columnEnds.length : column;
        item.column = targetColumn;
        columnEnds[targetColumn] = item.end;
      });

      group.items.forEach((item) => {
        item.columnCount = columnEnds.length;
      });
    });

    return positionedEvents;
  };

  const getCurrentTimePosition = () => {
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    return (currentHour * hourHeight) + (currentMinute * hourHeight / 60);
  };

  const timeLineTop = getCurrentTimePosition();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", bgcolor: "background.paper", borderRadius: 1, minHeight: 0 }}>
      {/* HEADER DE DÍAS */}
      <Box sx={{ display: "flex", borderBottom: `1px solid ${theme.palette.divider}`, pt: 1, pr: `${scrollbarWidth}px` }}>
        <Box sx={{ width: 44, flexShrink: 0, borderRight: `1px solid ${theme.palette.divider}` }} />
        {daysOfWeek.map((date, index) => {
          const d = date.getDate();
          const m = date.getMonth();
          const y = date.getFullYear();
          const today = isToday(d, m, y);

          return (
            <Box key={index} sx={{ flex: 1, p: 1, textAlign: "center", borderRight: index < 6 ? `1px solid ${theme.palette.divider}` : "none", cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }} onClick={() => onDateClick && onDateClick(new Date(y, m, d))}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500, textTransform: "uppercase", display: "block" }}>
                {DAYS_LABELS[index]}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
                <Typography variant="h6" sx={{ color: today ? "primary.contrastText" : "text.primary", bgcolor: today ? "primary.main" : "transparent", width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: today ? "bold" : "normal", lineHeight: 1 }}>
                  {d}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* FILA TODO EL DÍA */}
      <Box sx={{ display: "flex", borderBottom: `1px solid ${theme.palette.divider}`, minHeight: 42, bgcolor: "#fafafa", pr: `${scrollbarWidth}px` }}>
        <Box sx={{ width: 44, flexShrink: 0, borderRight: `1px solid ${theme.palette.divider}`, px: 0.25, py: 0.5, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: "bold", textAlign: "center", fontSize: "0.58rem", lineHeight: 1.05, textTransform: "uppercase" }}>
            Todo<br />el día
          </Typography>
        </Box>
        {daysOfWeek.map((date, index) => {
          const dayEvents = getEventsForDay(date);
          const allDayEvents = dayEvents.filter(evt => !evt.horaInicio);
          
          return (
            <Box key={index} sx={{ flex: 1, minWidth: 0, borderRight: index < 6 ? `1px solid ${theme.palette.divider}` : "none", p: 0.5, bgcolor: allDayEvents.length > 0 ? "#fffde7" : "transparent" }}>
              <Stack spacing={0.5} sx={{ width: "100%", minWidth: 0 }}>
                {allDayEvents.map((evt, i) => {
                  const eventColor = evt.color || theme.palette.primary.main;
                  return (
                    <Tooltip key={evt.id || `allday-${i}`} title={evt.titulo || ""} arrow enterDelay={350}>
                      <Box
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); if (onEventClick) onEventClick(evt); }}
                        sx={{
                          bgcolor: `${eventColor}33`,
                          color: eventColor,
                          borderLeft: `3px solid ${eventColor}`,
                          width: "100%",
                          maxWidth: "100%",
                          minWidth: 0,
                          boxSizing: "border-box",
                          px: 0.5,
                          py: 0.15,
                          borderRadius: "4px",
                          fontSize: "0.64rem",
                          fontWeight: "bold",
                          cursor: "pointer",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          lineHeight: 1.15,
                          "&:hover": { filter: "brightness(0.95)", boxShadow: 2 },
                        }}
                      >
                        {evt.titulo}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Box>

      {/* CUERPO - CUADRÍCULA DE HORARIOS */}
      <Box sx={{ display: "flex", height: { xs: 320, lg: 380 }, overflowY: "auto", position: "relative" }}>
        {/* Columna de Horas */}
        <Box sx={{ width: 44, flexShrink: 0, borderRight: `1px solid ${theme.palette.divider}`, position: "relative", bgcolor: "background.paper", zIndex: 2 }}>
          {hours.map(hour => (
            <Box key={hour} sx={{ height: hourHeight, position: "relative" }}>
              <Typography variant="caption" sx={{ position: "absolute", top: 2, right: 4, color: "text.secondary", fontWeight: "bold", bgcolor: "background.paper", px: 0.25, fontSize: "0.68rem" }}>
                {String(hour).padStart(2, '0')}:00
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Columnas de Días */}
        <Box sx={{ display: "flex", flex: 1, position: "relative", height: `${dayHeight}px` }}>
          {/* Líneas Horizontales de Fondo */}
          <Box sx={{ 
            position: "absolute", top: 0, left: 0, right: 0, height: `${dayHeight}px`, zIndex: 0, pointerEvents: "none",
            backgroundImage: `linear-gradient(${theme.palette.divider} 1px, transparent 1px)`,
            backgroundSize: `100% ${hourHeight}px`,
            backgroundPositionY: `${hourLineOffset}px`,
          }} />

          {daysOfWeek.map((date, index) => {
            const dayEvents = getEventsForDay(date);
            const timedEvents = layoutTimedEvents(dayEvents.filter(evt => !!evt.horaInicio));
            const today = isToday(date.getDate(), date.getMonth(), date.getFullYear());

            return (
              <Box 
                key={index} 
                onClick={() => onDateClick && onDateClick(new Date(date.getFullYear(), date.getMonth(), date.getDate()))}
                sx={{ 
                  flex: 1, 
                  borderRight: index < 6 ? `1px solid ${theme.palette.divider}` : "none", 
                  position: "relative",
                  height: `${dayHeight}px`,
                  zIndex: 1,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.02)" }
                }}
              >
                {/* Línea de tiempo actual solo para el día de hoy */}
                {today && timeLineTop !== null && (
                  <Box sx={{
                    position: "absolute",
                    top: timeLineTop,
                    left: 0,
                    right: 0,
                    height: "2px",
                    bgcolor: "red",
                    zIndex: 10,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: -4,
                      top: -3,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "red",
                    }
                  }} />
                )}

                {timedEvents.map(({ evt, top, height, column, columnCount, continuesAfterDay }, i) => {
                  const eventColor = evt.color || theme.palette.primary.main;
                  const isOverlapping = columnCount > 1;
                  const visibleHeight = Math.max(height - 2, 1);

                  return (
                    <Tooltip key={evt.id || `timed-${i}`} title={evt.titulo || ""} arrow enterDelay={350}>
                    <Paper
                      tabIndex={0}
                      elevation={3}
                      onClick={(e) => { e.stopPropagation(); if (onEventClick) onEventClick(evt); }}
                      sx={{
                        position: "absolute",
                        top: top,
                        height: visibleHeight, // Margen para que no toque la línea de abajo
                        left: isOverlapping ? `calc(${(column * 100) / columnCount}% + 2px)` : 2,
                        right: isOverlapping ? "auto" : 2,
                        width: isOverlapping ? `calc(${100 / columnCount}% - 4px)` : "auto",
                        bgcolor: `${eventColor}1A`,
                        color: eventColor,
                        borderLeft: `4px solid ${eventColor}`,
                        p: 0.5,
                        borderRadius: "4px",
                        borderBottomLeftRadius: continuesAfterDay ? 0 : "4px",
                        borderBottomRightRadius: continuesAfterDay ? 0 : "4px",
                        cursor: "pointer",
                        minWidth: 0,
                        boxSizing: "border-box",
                        overflow: "hidden",
                        transition: "all 0.2s",
                        "&:hover": {
                          filter: "brightness(0.95)",
                          zIndex: 30,
                          boxShadow: 6,
                        }
                      }}
                    >
                      <Typography variant="caption" display="block" sx={{ fontWeight: "bold", lineHeight: 1.2 }}>
                        {formatTimeHM(evt.horaInicio)} {evt.horaFin ? `- ${formatTimeHM(evt.horaFin)}` : ""}
                      </Typography>
                      <Typography variant="body2" noWrap sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2, fontWeight: 500 }}>
                        {evt.titulo}
                      </Typography>
                    </Paper>
                    </Tooltip>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
